import { html, nothing, render, svg } from 'lit-html';
import { Canvas, FabricImage, FabricObject, Group, Path, Textbox, TSVGExportOptions } from 'fabric';
import * as fabric from 'fabric';
import './fabricjs-object-extensions.js';
import * as opentype from 'opentype.js';
import { ProductDesignerApi } from '../../../../api/archive/product/product-designer-api.js';
import { IStandardApiError } from '../../../../api/base/i-standard-api-error.js';
import { IDesignerProductDetails } from '../../../../api/archive/product/models/designer-product-details.js';
import { ProductDesignerTextEditor } from './product-designer-text-editor.js';
import { ProductDesignerImageEditor } from './product-designer-image-editor.js';
import { Notify } from '../../../../utils/notify.js';
import { IImageCategory } from '../../../../api/archive/product/models/image-category.js';
import { ProductDesignerHistoryManager } from './product-designer-history-manager.js';
import { ProductDesignerObjectMenu } from './product-designer-object-menu.js';
import { initI18Next, NS, createTranslator } from '../../../../utils/init-i18next.js';
import { CurvedText } from './curved-text.js';
import { DEFAULT_CANVAS_HEIGHT, DEFAULT_CANVAS_WIDTH, ColorMode } from '../../../../interfaces/archive/product/general-settings.js';
import { ColorPaletteType } from '../../../../interfaces/archive/product/color-palette.js';
import { IProductPrintConfig, IProductVariantPrintConfig, PrintConfigMode } from '../../../../interfaces/archive/product/product-print-config.js';
import { getImageUrlFromId, createBuiltInShape, isBuiltInShapeId, Section, updateObjectControlsVisibility, icon, getNextFreeObjectId, getSectionFromObjectType, updateObjectCanvasLocks, SVG_MIME_TYPE, getFontPath } from './fabricjs-canvas-helpers.js';
import { ProductDesignerShapeObjectEditor } from './product-designer-shape-object-editor.js';
import { ProductDesignerMeasurementsCalculator } from './product-designer-measurements-calculator.js';
import { ProductDesignerColorSection } from './product-designer-color-section.js';
import { getWebSafeFontsFileNames } from '../../../../utils/font-utils.js';
import { ProductDesignerRulers } from './product-designer-rulers.js';
import { ImageCropDialog } from '../../image/image-crop-dialog.js';
import { ProductDesignerExportDialog } from './product-designer-export-dialog.js';
import { ProductDesignerPreviewDialog } from './product-designer-preview-dialog.js';
import { ProductDesignerHelpDialog } from './product-designer-help-dialog.js';
import { ProductDesignerCanvasUtils } from './product-designer-canvas-utils.js';
import { ProductDesignerObjectList } from './product-designer-object-list.js';
import { ProductDesignerAttachmentsList } from './product-designer-attachments-list.js';
import { ProductDesignerBackgroundShapeList } from './product-designer-background-shape-list.js';
import { IProductDesignerConfig } from './interfaces/product-designer-config.js';
import { createRef, ref, Ref } from 'lit-html/directives/ref.js';
import { ProductDesignerGuidelinesHandler } from './product-designer-guidelines-handler.js';

export class ProductDesigner extends HTMLElement {
    private readonly BACKGROUND_MAX_SIZE_FACTOR = 0.9;

    constructor() {
        super();
    }

    /* #region Public */
    public errorReporter: (error: IStandardApiError) => void = (error) => Notify.getInstance().error(error.message);

    public async setProductId(productId: number) {
        this._productId = productId;

        if (this._productId <= 0) {
            return;
        }

        if (!this._isInitialized) {
            await this.initInternal();
        } else {
            await this.loadData();
            await this.updateFonts();

            // Update background
            await this.updateCanvasBackground();
            await this.updateCanvasOverlay();

            await this.updateAttachmentsList();
            this.refreshEditors();

            this._canvas.renderAll();
        }

        this.render();
    }

    public async refresh() {
        if (this._productId <= 0 || !this._isInitialized) {
            return;
        }

        await this.loadData();
        await this.updateFonts();
        await this.updateCanvasBackground();
        await this.updateCanvasOverlay();
        this.refreshEditors();

        this._canvas.renderAll();

        this.render();
    }

    private _design: string = null;
    public getDesign(): string {
        if (this._canvas) {
            this._canvas.colorMode = this._productDetails?.printConfig.generalSettings.colorMode ?? null;
            const designObj = this._canvas.toObject(['attachmentZoneId', 'backgroundShapeId', 'monoColor', 'dualForegroundColor', 'colorMode']);

            // Clear background and overlay images from the design object
            if (designObj.backgroundImage) {
                delete designObj.backgroundImage;
            }

            if (designObj.overlayImage) {
                delete designObj.overlayImage;
            }

            this._design = JSON.stringify(designObj);
        }

        return this._design;
    }

    public async clearDesign(addDefaultText: boolean = true) {
        this._design = null;
        await this.clear(true, addDefaultText);
        this.render();
    }

    public async setDesign(design: string) {
        this._design = design;

        await this.clear(true, false);
        await this.loadState(design);

        await this.updateCanvasBackground();
        await this.updateCanvasOverlay();
        this._colorSection.refresh();

        this._canvas?.renderAll();

        this.render();
        this.refreshLists();

        // Reset history manager
        this._stateHistoryManager.clearHistory();
        this._stateHistoryManager.addState(this.getDesign());
    }

    public async confirmDesign(): Promise<boolean> {
        if (this._canvas.confirmed) {
            return true;
        }

        const confirmed = await this._previewDialog.confirm();
        if (confirmed) {
            this._canvas.confirmed = true;
        }

        return confirmed;
    }

    public set exportFilenamePrefix(prefix: string) {
        this._config.exportFilenamePrefix = prefix;

        if (this._exportDialog) {
            this._exportDialog.filenamePrefix = prefix;
        }

        if (this._previewDialog) {
            this._previewDialog.filenamePrefix = prefix;
        }
    }

    public get exportFilenamePrefix(): string {
        return this._config.exportFilenamePrefix;
    }

    public getAttachmentZoneProduct() {
        return this._canvasUtils.getAttachmentZoneProduct();
    }

    public hasChanges(): boolean {
        return this._stateHistoryManager.canUndo();
    }

    public onAttachmentChanged?: (attachmentZoneId: number | null) => void = null;
    
    private startDesign?: string;
    public setStartDesign(design: string) {
        this.startDesign = design;
    }

    private _printConfigOverride: IProductPrintConfig = null;
    public setPrintConfigOverride(override: IProductPrintConfig | null) {
        if (this._printConfigOverride === override) {
            return;
        }

        this._printConfigOverride = override;

        if (this._productDetails?.printConfig && override) {
            Object.assign(this._productDetails.printConfig, override);
            this.applyVariantOverrideToProductDetails();
            this.refreshEditors();
        }
    }

    private _variantOverride: IProductVariantPrintConfig = null;
    public setVariantOverride(override: IProductVariantPrintConfig | null) {
        if (this._variantOverride === override) {
            return;
        }

        this._variantOverride = override;

        if (this._productDetails) {
            this.applyVariantOverrideToProductDetails();
        }

        this.updateCanvasBackground();
        this._canvas?.renderAll();
    }

    private applyVariantOverrideToProductDetails() {
        const override = this._variantOverride;
        if (!override || !this._productDetails) {
            return;
        }

        if (override.canvasBackgroundImageId > 0) {
            this._productDetails.printConfig.generalSettings.canvasBackgroundImageId = override.canvasBackgroundImageId;
        }
        if (override.backgroundWidthMm > 0) {
            this._productDetails.printConfig.generalSettings.backgroundWidthMm = override.backgroundWidthMm;
        }
        if (override.backgroundHeightMm > 0) {
            this._productDetails.printConfig.generalSettings.backgroundHeightMm = override.backgroundHeightMm;
        }
    }

    public async getSvg(withBackground: boolean = true, withOverlay: boolean = true): Promise<string> {
        // Create a clone to use for export since some objects may be modified
        const canvas = await this._canvas.clone([]);

        // Download and convert all image urls to data urls
        const imageObjects = canvas.getObjects().filter(obj => obj.isImageType() && !obj.isSvgGroup) as FabricImage[];
        const imageFetchPromises = imageObjects.map((image) => {
            const url = image.getSrc();
            return fetch(url).then(res => res.blob()).then(blob => this.blobToBase64(blob)).then(base64 => ({ id: image.id, dataUrl: base64, isBackground: false, isOverlay: false }));
        });

        // Append the background image if it exists
        const backgroundObject = canvas.backgroundImage;
        if (canvas.backgroundImage instanceof FabricImage) {
            const bgImg = backgroundObject as FabricImage;
            if (bgImg) {
                const url = bgImg.getSrc();
                imageFetchPromises.push(fetch(url).then(res => res.blob()).then(blob => this.blobToBase64(blob)).then(base64 => ({ id: -1, dataUrl: base64, isBackground: true, isOverlay: false })));
            }
        }

        const overlayGroup = canvas.overlayImage as Group;
        const overlayImages = overlayGroup?.getObjects().filter(obj => obj.isImageType() && !obj.isSvgGroup) as FabricImage[];
        if (overlayImages?.length > 0) {
            for (const overlayImage of overlayImages) {
                const url = overlayImage.getSrc();
                const index = overlayImages.indexOf(overlayImage);
                imageFetchPromises.push(fetch(url).then(res => res.blob()).then(blob => this.blobToBase64(blob)).then(base64 => ({ id: -1-index, dataUrl: base64, isBackground: false, isOverlay: true })));
            }
        }

        // Update all image urls to data urls
        await Promise.all(imageFetchPromises).then(async (imageDatas) => {
            for (const imageData of imageDatas) {
                if (imageData.isBackground && backgroundObject && backgroundObject instanceof FabricImage) {
                    await backgroundObject.setSrc(imageData.dataUrl);
                }
                else if (imageData.isOverlay && overlayImages?.length > 0) {
                    const index = Math.abs(imageData.id) - 1;
                    const overlayImage = overlayImages[index];
                    if (overlayImage) {
                        await overlayImage.setSrc(imageData.dataUrl);
                    }
                } else if (!imageData.isBackground && !imageData.isOverlay) {
                    const image = canvas.getObjects().find(o => o.id === imageData.id) as FabricImage;
                    if (image) {
                        await image.setSrc(imageData.dataUrl);
                    }
                }
            }
        });

        // Hide background image if not needed
        if (backgroundObject && !withBackground) {
            backgroundObject.set({ visible: false });
        }

        // Hide overlay image if not needed
        const overlayObjects = overlayGroup?.getObjects().filter(obj => obj.isImageType()) as FabricObject[];
        if (overlayObjects?.length > 0 && !withOverlay) {
            for (const overlayObject of overlayObjects) {
                overlayObject.set({ visible: false });
            }
        }

        // Rerender curved text objects in high resolution
        const curvedTextObjects = canvas.getObjects().filter(obj => obj.type.toLowerCase() === CurvedText.type.toLowerCase()) as CurvedText[];
        for (const curvedText of curvedTextObjects) {
            curvedText.setSizeMultiplier(4);
        }

        // Convert text objects to paths and remove empty
        const fontDataMap = Object.fromEntries(this._fontData.map(f => [f.name, f.data]));
        const textBoxes = canvas.getObjects().filter(obj => obj.type.toLowerCase() === Textbox.type.toLowerCase()) as (Textbox)[];
        for (const textBox of textBoxes) {
            if (!textBox.text || !textBox.text.trim().length) {
                canvas.remove(textBox);
                continue;
            }

            const svgPath = await this.convertTextToPaths(textBox, fontDataMap);
            if (svgPath) {
                const indexOfTextBox = canvas.getObjects().indexOf(textBox);
                canvas.remove(textBox);
                canvas.insertAt(indexOfTextBox, svgPath);
            }
        }

        // Export the canvas to SVG
        const objectsBoundingBox = backgroundObject?.getBoundingRect();
        const width = this._mCalc?.isConfigured ? `${this._mCalc.context.physicalWidth}mm` : (objectsBoundingBox?.width ?? canvas.width).toString();
        const height = this._mCalc?.isConfigured ? `${this._mCalc.context.physicalHeight}mm` : (objectsBoundingBox?.height ?? canvas.height).toString();
        let svg = objectsBoundingBox ? 
            canvas.toSVG({
                viewBox: {
                    x: objectsBoundingBox.left,
                    y: objectsBoundingBox.top,
                    width: objectsBoundingBox.width,
                    height: objectsBoundingBox.height
                },
                width: width,
                height: height
            } as TSVGExportOptions) 
            : canvas.toSVG();

        // Dispose of the canvas to free up memory
        canvas.dispose();

        return svg;
    }

    /**
     * Lenient font-buffer lookup: tries the literal font-family first, then a normalised form
     * (case-folded, quotes stripped, CSS fallbacks dropped). Fabric occasionally emits the family
     * with stray quotes or a `Family, sans-serif` fallback list — strict map lookup misses those.
     */
    private resolveFontBuffer(fontFamily: string, fontDataMap: { [name: string]: ArrayBuffer }): ArrayBuffer | null {
        if (!fontFamily) return null;
        if (fontDataMap[fontFamily]) return fontDataMap[fontFamily];

        const normalise = (s: string) => s.replace(/^['"]|['"]$/g, '').trim().toLowerCase();
        const primary = normalise(fontFamily.split(',')[0]);
        for (const key of Object.keys(fontDataMap)) {
            if (normalise(key) === primary) return fontDataMap[key];
        }
        return null;
    }

    private addCustomFontsToSvg(svg: string) {
        const svgDoc = new DOMParser().parseFromString(svg, 'image/svg+xml');

        // Find any text elements in the SVG
        const textElements = svgDoc.querySelectorAll('text, tspan, textPath');

        // Check attribute "font-family" for each text element
        const fontFamilies: string[] = []; 
        textElements.forEach((textElement) => {
            const fontFamily = textElement.getAttribute('font-family');
            if (fontFamily && !fontFamilies.includes(fontFamily)) {
                fontFamilies.push(fontFamily);
            }
        });

        if (!fontFamilies.length) {
            return svg;
        }

        // Create a style element to hold the @font-face rules
        const styleElement = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'style');

        // Add @font-face rules for each font family, creating a base64 data URL for each font
        styleElement.textContent = fontFamilies.map((fontFamily) => {
            const fontData = this._fontData.find(font => font.name === fontFamily);
            if (!fontData) {
                return '';
            }

            const base64Font = btoa(String.fromCharCode(...new Uint8Array(fontData.data)));
            const format = fontData.extension === 'woff' ? 'woff' : fontData.extension === 'woff2' ? 'woff2' : fontData.extension === 'otf' ? 'opentype' : 'truetype';
            return `@font-face { font-family: '${fontFamily}'; src: url('data:font/${format};charset=utf-8;base64,${base64Font}') format(${format}); }`;
        }).join('\r\n');

        // Append the style element to the SVG document
        svgDoc.documentElement.appendChild(styleElement);

        // Serialize the modified SVG document back to a string
        const serializer = new XMLSerializer();
        const modifiedSvg = serializer.serializeToString(svgDoc);

        return modifiedSvg;
    }

    private blobToBase64(blob: Blob) {
        return new Promise<string>((resolve, _) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Converts a Fabric.js text object to an image object.
     * This is particularly useful for flattening text into a non-editable graphic.
     * @param {TextBox | Group} textToConvert - The Fabric.js text to convert.
     */
    private async convertTextToImage(textToConvert: Textbox | Group): Promise<FabricImage> {
        // Isolate the object in a temporary canvas
        const tempCanvas = new Canvas(null, {
            width: textToConvert.getScaledWidth(),
            height: textToConvert.getScaledHeight(),
        });
    
        // Clone the object to the temporary canvas to preserve its state
        let clonedObject = await textToConvert.clone([]);
        clonedObject.set({
            left: clonedObject.width / 2,
            top: clonedObject.height / 2,
            angle: 0,
            originX: 'center',
            originY: 'center',
        });
        tempCanvas.add(clonedObject);

        // 2. Export the temporary canvas to an SVG string
        let svgString = tempCanvas.toSVG();
        svgString = this.addCustomFontsToSvg(svgString);
        const encodedSvg = 'data:image/svg+xml;base64,' + window.btoa(svgString);

        // 3. Load the SVG string back as a Fabric.js Image object
        let resultImage = await FabricImage.fromURL(encodedSvg);
        // Set the new image's position to match the original object
        resultImage.set({
            left: textToConvert.left,
            top: textToConvert.top,
            angle: textToConvert.angle,
            scaleX: textToConvert.scaleX,
            scaleY: textToConvert.scaleY,
            fill: textToConvert.fill,
            stroke: textToConvert.stroke,
            strokeWidth: textToConvert.strokeWidth,
            originX: textToConvert.originX,
            originY: textToConvert.originY,
        });

        // Dispose of the canvas to free up memory
        tempCanvas.dispose();

        return resultImage;
    }

    /**
     * Converts a Fabric.js Textbox to separate SVG paths for each letter using opentype.js.
     * Returns a Fabric.Group containing all letter paths, positioned to match the original text.
     */
    private async convertTextToPaths(textBox: Textbox, fontDataMap: { [fontFamily: string]: ArrayBuffer }): Promise<fabric.Group | null> {
        const fontFamily = textBox.fontFamily || 'Arial';
        const fontSize = textBox.fontSize || 40;
        const fontBuffer = this.resolveFontBuffer(fontFamily, fontDataMap);
        if (!fontBuffer) {
            console.warn(`convertTextToPaths: no font buffer for '${fontFamily}' — text will fall back to <text>`);
            return null;
        }

        // Load font
        const font = opentype.parse(fontBuffer);

        // Note: the coefficient for space width 1000 was found in the fabric.js source code and docs.
        // Link: https://fabricjs.com/api/classes/textbox/#charspacing
        const unitWidth = 1 / font.unitsPerEm * fontSize;
        const spaceWidth = fontSize * textBox.charSpacing / 1000; 

        const textLineGroups: fabric.Group[] = [];
        let y = 0;

        for (var ti = 0; ti < textBox.textLines.length; ti++) {

            const textLine = textBox.textLines[ti];
            const glyphs = font.stringToGlyphs(textLine);

            const letterPaths: fabric.Path[] = [];
            let textLineX = 0;

            for (var gi = 0; gi < glyphs.length; gi++) {
                const glyph = glyphs[gi];
                const path = glyph.getPath(textLineX, 0, fontSize);
                const svgPathData = path.toPathData(4);

                const letterPath = new fabric.Path(svgPathData, {
                    fill: textBox.fill,
                    stroke: textBox.stroke,
                    strokeWidth: textBox.strokeWidth,
                    scaleX: textBox.scaleX,
                    scaleY: textBox.scaleY,
                });

                // Add spaceWidth before each glyph except the first
                if (gi > 0) {
                    textLineX += spaceWidth;
                }

                // Offset the letter horizontally with leftSideBearing
                const leftSideBearing = glyph.leftSideBearing * unitWidth;
                letterPath.set({ left: (textBox.left + textLineX + leftSideBearing) });

                letterPaths.push(letterPath);

                // Advance x for next letter (offset letter width)
                textLineX += glyph.advanceWidth * unitWidth;
            }

            if (textBox.underline) {
                const underlinePath = createUnderlinePath(textBox, 0, 0, fontSize, textLineX);
                letterPaths.push(underlinePath);
            }

            const letterPathsGroup = new fabric.Group(letterPaths, {
                top: textBox.top + y,
            });

            applyTextWeight(textBox, letterPathsGroup, fontSize);
            applyTextStyle(textBox, letterPathsGroup);

            const lineHeight = textBox.getHeightOfLine(ti);

            const verticalAlignOffset = (lineHeight - lineHeight / textBox.lineHeight) / 2;
            let horizontalAlignOffset = 0;

            if (textBox.textAlign === 'center') {
                horizontalAlignOffset = (textBox.width - textLineX) / 2;
            } else if (textBox.textAlign === 'right') {
                horizontalAlignOffset = textBox.width - textLineX;
            }

            // Apply align offsets to each line
            letterPathsGroup.getObjects().forEach((obj) => {
                obj.set({
                    top: obj.top + verticalAlignOffset,
                    left: obj.left + horizontalAlignOffset,
                });
            });

            textLineGroups.push(letterPathsGroup);

            y += lineHeight;
        }

        // Result group of paths
        let resultGroup = new fabric.Group(textLineGroups, {
            left: textBox.left,
            top: textBox.top,
            angle: textBox.angle,
        });

        return resultGroup;

        function createUnderlinePath(textBox: Textbox, x: number, y: number, fontSize: number, width: number): fabric.Path {
            const underlineY = y + fontSize * 0.15;
            const underlineHeight = Math.max(1, fontSize * 0.07); // Thickness of underline

            const underlinePathData = `M${x},${underlineY} L${x + width},${underlineY}`;
            const underlinePath = new fabric.Path(underlinePathData, {
                fill: textBox.fill,
                stroke: textBox.fill,
                strokeWidth: underlineHeight,
            });

            underlinePath.set({ left: (textBox.left + x) });

            return underlinePath;
        }

        function applyTextWeight(textBox: Textbox, group: fabric.Group, fontSize: number) {
            if (textBox.fontWeight && typeof textBox.fontWeight === 'string' && textBox.fontWeight == 'bold') {
                group.getObjects().forEach((obj) => {
                    obj.set({
                        stroke: textBox.fill,
                        strokeWidth: obj.strokeWidth + fontSize * 0.008
                    });
                });
            }
        }

        function applyTextStyle(textBox: Textbox, object: fabric.Object) {
            if (textBox.fontStyle && typeof textBox.fontStyle === 'string' && textBox.fontStyle == 'italic') {
                object.set({
                    skewX: -12, // Apply the skew for italic effect
                });
            }
        }
    }

    /* #endregion */

    /* #region Markup */
    private template = (t: (key: string) => string) => html`
        <div class="product-designer" ${ref(this._mainContainerRef)}>
            ${this._config.useDefaultStyles ? html`
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap" rel="stylesheet">
                <link rel="stylesheet" href="/components-shared/elements/archive/product/product-design/product-designer.css">
            ` : nothing}
            <div class="head-actions flex flex-gap flex-row">
                <button @click=${this.onResetClicked.bind(this)}>${icon('action/trash.svg')} ${t('startAgain')}</button>
                <button @click=${this.onUndoClicked.bind(this)} ?disabled=${!this.canUndo()}>${icon('action/arrow-undo.svg')} ${t('undo')}</button>
                <button @click=${this.onRedoClicked.bind(this)} ?disabled=${!this.canRedo()}>${icon('action/arrow-redo.svg')} ${t('redo')}</button>
                ${this._config.adminMode ? html`
                    <button @click=${this.onExportClicked.bind(this)} ?disabled=${!this._canvas?.getObjects()?.length}>${icon('download.svg')} ${t('export')}</button>
                ` : html`
                    <button @click=${this.onPreviewClicked.bind(this)} ?disabled=${!this._canvas?.getObjects()?.length}>${icon('action/preview.svg')} ${t('preview')}</button>
                `}
                ${!this._config.adminMode && this._productDetails.printConfig.generalSettings.designerGuideDocumentId ? html`
                    <button class="btn-help" @click=${this.onHelpClicked.bind(this)}>${icon('action/help.svg')} ${t('help')}</button>
                ` : nothing}
            </div>
            <div class="edit-container flex flex-row">
                <div class="section-selector">
                    ${this._productDetails.printConfig.generalSettings.shapes?.some(s => s.enabled) ? html`
                        <button class="flex flex-col flex-center ${this._activeSection === Section.BackgroundShape ? 'active' : ''}" @click="${this.onSectionClicked.bind(this, Section.BackgroundShape)}">${icon('sidebar/shape.svg')}<span>${t('backgroundShape')}</span></button>
                    ` : nothing}
                    ${this._productDetails.printConfig.generalSettings.colorMode !== ColorMode.BackgroundBased ? html`
                        <button class="flex flex-col flex-center ${this._activeSection === Section.Color ? 'active' : ''}" @click="${this.onSectionClicked.bind(this, Section.Color)}">${icon('sidebar/color.svg')}<span>${t('color')}</span></button>
                    ` : nothing}
                    ${this._productDetails.printConfig.advancedTextSettings.enabled ? html`
                        <button class="flex flex-col flex-center ${this._activeSection === Section.Text ? 'active' : ''}" @click="${this.onSectionClicked.bind(this, Section.Text)}">${icon('sidebar/text.svg')}<span>${t('text')}</span></button>
                    ` : nothing}
                    ${this._productDetails.printConfig.imageSettings.enabled ? html`
                        <button class="flex flex-col flex-center ${this._activeSection === Section.Image ? 'active' : ''}" @click="${this.onSectionClicked.bind(this, Section.Image)}">${icon('sidebar/image.svg')}<span>${t('image')}</span></button>
                    ` : nothing}
                    ${this._productDetails.printConfig.objectSettings.enabled ? html`
                        <button class="flex flex-col flex-center ${this._activeSection === Section.ShapeObject ? 'active' : ''}" @click="${this.onSectionClicked.bind(this, Section.ShapeObject)}">${icon('sidebar/object.svg')}<span>${t('shapeObject')}</span></button>
                    ` : nothing}
                    ${this._canvasUtils?.anyAttachments() && !this._config.exclusionZoneMode ? html`
                        <button class="flex flex-col flex-center ${this._activeSection === Section.Attachment ? 'active' : ''}" @click="${this.onSectionClicked.bind(this, Section.Attachment)}">${icon('sidebar/attachment.svg')}<span>${t('attachment')}</span></button>
                    ` : nothing}
                </div>
                <div class="sections">
                    <div class="section">
                        ${this._activeSection === Section.BackgroundShape ?
                            this._backgroundShapeList
                        : this._activeSection === Section.Color ?
                            this._colorSection
                        : this._activeSection === Section.Text ? 
                            this._activeObject ? this._textEditor : this._textList
                        : this._activeSection === Section.Image ? 
                            this._activeObject ? this._imageEditor : this._imageList
                        : this._activeSection === Section.ShapeObject ? 
                            this._activeObject ? this._shapeObjectEditor : this._shapeObjectList
                        : this._activeSection === Section.Attachment ? 
                            this._attachmentsList
                        : nothing}
                    </div>
                </div>
                <div class="editor-canvas" ${ref(this._canvasContainerRef)}>
                    ${this._canvasElement}
                    ${this._rulers}
                    ${this._objectMenu}
                </div>
            </div>
            ${this._exportDialog}
            ${this._previewDialog}
            ${this._helpDialog}
            ${this._cropDialog}
        </div>
    `;

    /* #endregion */

    /* #region Event handlers */

    private onSectionClicked(section: Section, event?: Event) {
        event?.preventDefault();
        this._activeSection = section
        this.clearSelection();

        this.render();
    }

    private async onAddNewClicked(section: Section) {
        switch (section) {
            case Section.Text:
                this._textEditor.addNewText();
                break;
            case Section.Image:
                await this._imageEditor.addNewImage();
                break;
            case Section.ShapeObject:
                await this._shapeObjectEditor.addNewShapeObject();
                break;
            default:
                break;
        }
    }

    private onCanvasSelectionChanged(event: any) {
        const activeObject = event.selected[0];

        if (!activeObject) {
            this.clearSelection();
            return;
        }

        this.setActiveObject(activeObject);
    }

    private onCanvasSelectionCleared() {
        this.clearSelection();
    }

    private async onDocumentKeyDown(event: KeyboardEvent) {
        if (!this.checkVisibility()) {
            return;
        }

        if (event.key === 'Delete') {
            if (this._activeObject.isTextType()) {
                const text = this._activeObject.asTextType();
                if (text.isEditingText || text.isEditing) {
                    return;
                }
            }

            this.deleteActiveObject();
        } else if (event.ctrlKey && event.key === 'z') {
            await this.historyUndo();
        } else if (event.ctrlKey && event.key === 'y') {
            await this.historyRedo();
        }
    }

    private async onResetClicked(event: Event) {
        event.preventDefault();
        await this.clear(false, true);
        this._stateHistoryManager.addState(this.startDesign || this.getDesign());
        if (this.startDesign) {
            await this.setDesign(this.startDesign);
        }
        this.render();
    }

    private async onUndoClicked(event: Event) {
        event.preventDefault();
        await this.historyUndo();
    }

    private async onRedoClicked(event: Event) {
        event.preventDefault();
        await this.historyRedo();
    }

    private async onExportClicked(event: Event) {
        event.preventDefault();
        await this._exportDialog.showModal();
    }

    private async onPreviewClicked(event: Event) {
        event.preventDefault();
        await this._previewDialog.showModal();
    }

    private async onHelpClicked(event: Event) {
        event.preventDefault();
        await this._helpDialog.showModal();
    }

    private onCanvasObjectModified() {
        this._stateHistoryManager.addState(this.getDesign());
        this.render();
    }

    private onCanvasObjectRemoved(ev: { target: FabricObject }) {
        if (ev.target) {
            const children = this._canvas.getObjects().filter(o => o.parentId === ev.target.id);
            children.forEach(child => this._canvas.remove(child));
        }
    }

    private async onSingleColorChange(_: Event) {
        await this.updateObjectsSingleColor();
        this.refreshEditors();
        await this.updateCanvasBackground();
        this._canvas.renderAll();
        this.render();
    }

    private async updateObjectsSingleColor() {
        const imageUpdatePromises: Promise<void>[] = [];
        const svgPostFixes: { svg: Group; left: number; top: number; scaledWidth: number }[] = [];

        const foregroundColor = this._canvas.getForegroundColor();
        const colorMode = this._productDetails?.printConfig.generalSettings.colorMode;
        const imageColor = colorMode === ColorMode.DualColor ? this._canvas.getForegroundColor()
            : colorMode === ColorMode.SingleColor ? this._canvas.getMonoColor()
            : null;

        // No canvas-level color resolved (e.g. mode mismatch on template load and no defaults
        // configured). Leave existing object fills intact rather than nulling them out.
        if (!foregroundColor) {
            return;
        }

        // Set color of objects
        this._canvas.getObjects().forEach(async obj => {
            if (obj.isTextType()) {
                obj.set({ fill: foregroundColor });
                // Per-character `styles` entries on a Textbox override the textbox-level fill, so
                // designs with mixed font sizes / weights kept their original colour. Strip the
                // per-character fill so the new textbox fill takes effect across every line.
                const textType = obj.asTextType?.();
                textType?.removeStyle?.('fill');
            } else if (obj.isShapeType()) {
                const shape = obj.asShapeType();

                // If not fill is transparent, set the fill color
                if (shape.fill && shape.fill !== 'transparent') {
                    shape.set({ fill: foregroundColor });
                }

                shape.set({ stroke: foregroundColor });
            } else if (obj.isImageType()) {
                const image = obj as FabricImage;
                const imageUrl = getImageUrlFromId(image.itemId, undefined, undefined, imageColor);

                if (image.isSvgGroup) {
                    imageUpdatePromises.push(image.updateSvgUrl(imageUrl));

                    // Store the svg postfix to adjust position later
                    svgPostFixes.push({ svg: (obj as Group), left: image.left, top: image.top, scaledWidth: image.getScaledWidth() });
                } else {
                    imageUpdatePromises.push(image.setSrc(imageUrl));
                }
            }
        });

        await Promise.all(imageUpdatePromises);

        // Adjust position of svg objects since the width may have changed due to color change
        for (const svgPostFix of svgPostFixes) {
            const svg = svgPostFix.svg;
            const left = svgPostFix.left;
            const top = svgPostFix.top;
            const scale = svgPostFix.scaledWidth / svg.width;
            svg.set({ left, top, scaleX: scale, scaleY: scale });
            svg.setCoords();
        }
    }

    private onEditorChange(_: Event) {
        this._objectMenu.updatePosition();
        
        if (this._activeObject) {
            updateObjectCanvasLocks(this._config.adminMode, this._activeObject);
            updateObjectControlsVisibility(this._config.adminMode, this._activeObject);
        }

        this._stateHistoryManager.addState(this.getDesign());

        this.render();
        this._canvas.renderAll();
    }

    private onObjectMenuChange() {
        this._stateHistoryManager.addState(this.getDesign());
        this.render();
    }

    private onAttachmentSelected(id: number) {
        this._canvas.attachmentZoneId = id === -1 ? null : id;
        this.updateCanvasOverlay();
        this._canvas.renderAll();
        this.render();

        this._stateHistoryManager.addState(this.getDesign());

        if (typeof this.onAttachmentChanged === 'function') {
            this.onAttachmentChanged(this._canvas.attachmentZoneId);
        }
    }

    private async onBackgroundShapeSelected(shapeId: number) {
        this._canvas.backgroundShapeId = shapeId;

        const shapeConfig = this._productDetails.printConfig.generalSettings.shapes?.find(s => s.shapeId === shapeId);
        const shapeDefinition = this._productDetails.printConfig.shapesLibrary?.find(d => d.id === shapeId);
        const imageId = shapeConfig?.backgroundImageIdOverride || shapeDefinition?.backgroundImageId || 0;

        this._productDetails.printConfig.generalSettings.canvasBackgroundImageId = imageId;

        await this.updateCanvasBackground();

        this._stateHistoryManager.addState(this.getDesign());

        this._backgroundShapeList?.refresh();
        this.render();
    }

    private async onCloneObjectClicked() {
        if (!this._canvas.getActiveObject()) {
            return;
        }

        const nextId = getNextFreeObjectId(this._canvas);
        const clone = await this._canvas.getActiveObject().clone();
        const controlsVisibility = this._canvas.getActiveObject()._controlsVisibility;
        const position = this.determineClonePosition(clone.left, clone.top);

        clone.id = nextId;
        clone.setControlsVisibility(controlsVisibility);
        clone.set({ left: position.left, top: position.top });
        this._canvas.add(clone);
        this._canvas.setActiveObject(clone);
        
        this._stateHistoryManager.addState(this.getDesign());
    }

    private determineClonePosition(originalLeft: number, originalTop: number): { left: number; top: number } {
        const offset = 15; // Offset by X pixels
        const outOfBoundsLimit = 50; // If the object is within X pixels of the edge, reset to 0

        let newLeft = originalLeft + offset;
        let newTop = originalTop + offset;

        // Ensure the new position is within canvas bounds
        const canvasWidth = this._canvas.getWidth();
        const canvasHeight = this._canvas.getHeight();

        if (newLeft + outOfBoundsLimit > canvasWidth || newTop + outOfBoundsLimit > canvasHeight) {
            newLeft = 0;
            newTop = 0;
        }

        return { left: newLeft, top: newTop };
    }

    private readonly MEDIUM_SCREEN_WIDTH = 1200;

    private readonly SMALL_SCALE_MIN = 1;
    private readonly SMALL_SCALE_MAX = 1200;

    private readonly DESKTOP_SCALE_MIN = 1200;
    private readonly DESKTOP_SCALE_MAX = 1509;

    private onContainerResized() {
        window.requestAnimationFrame(() => {
            const containerWidth = this.offsetWidth - 2; // -2 for borders
            const sectionSelectorEl = this._root.querySelector('.section-selector') as HTMLElement;
            const sectionsEl = this._root.querySelector('.sections') as HTMLElement;

            this._mainContainerRef.value?.classList.toggle('medium-screen', containerWidth < this.MEDIUM_SCREEN_WIDTH);

            let canvasScale = 1;
            if (containerWidth > this.SMALL_SCALE_MIN && containerWidth <= this.SMALL_SCALE_MAX) {
                canvasScale = Math.min(containerWidth / this._canvas.getWidth(), 1);
            } else if (containerWidth > this.DESKTOP_SCALE_MIN && containerWidth <= this.DESKTOP_SCALE_MAX) {
                const menuWidth = (sectionSelectorEl?.offsetWidth || 0) + (sectionsEl?.offsetWidth || 0) || 0;
                canvasScale = Math.min((containerWidth - menuWidth) / this._canvas.getWidth(), 1);
            }

            
            // Update controls size based on canvas scale
            this._canvas.getObjects().forEach(obj => {
                obj.cornerSize = 13 / canvasScale;
            });

            if (this._canvas.getActiveObject()) {
                this._canvas.requestRenderAll();
            }

            const cont = this._canvasContainerRef.value;

            if (cont) {
                const canvasWidth = this._canvas.getWidth();
                const canvasHeight = this._canvas.getHeight();
                const scaledWidth = canvasWidth * canvasScale;
                const scaledHeight = canvasHeight * canvasScale;
                const leftPos = (canvasWidth - scaledWidth) / -2;
                const topPos = (canvasHeight - scaledHeight) / -2;
                const mBottom = topPos * 2;

                cont.style.setProperty('scale', canvasScale.toString());
                cont.style.setProperty('top', `${topPos}px`);
                cont.style.setProperty('margin-bottom', `${mBottom}px`);

                if (containerWidth >= this.MEDIUM_SCREEN_WIDTH) {
                    sectionsEl?.style.setProperty('max-height', `${scaledHeight}px`);
                    cont.style.setProperty('left', `${leftPos}px`);
                }
                else {
                    sectionsEl?.style.removeProperty('max-height');
                    cont.style.removeProperty('left');
                }
            }

            if (this._objectMenu) {
                this._objectMenu.updateScale(canvasScale);
            }

            if (this._rulers) {
                this._rulers.updateScale(canvasScale);
            }
        });
    }

    /* #endregion */

    /* #region Private */
    private _isRendered: boolean = false;
    private _isInitialized: boolean = false;
    private _api: ProductDesignerApi;
    private _productDetails: IDesignerProductDetails;
    private _imageCategories: IImageCategory[];
    private _productId: number;
    private _config: IProductDesignerConfig = {
        adminMode: false,
        exclusionZoneMode: false,
        useDefaultStyles: false,
        exportFilenamePrefix: 'ProductDesign'
    };
    private _root: HTMLElement | DocumentFragment;
    private _activeSection: Section = Section.Text;
    private _activeObject: FabricObject | null = null;
    private _colorSection: ProductDesignerColorSection;
    private _textList: ProductDesignerObjectList;
    private _textEditor: ProductDesignerTextEditor;
    private _imageList: ProductDesignerObjectList;
    private _imageEditor: ProductDesignerImageEditor;
    private _shapeObjectList: ProductDesignerObjectList;
    private _shapeObjectEditor: ProductDesignerShapeObjectEditor;
    private _attachmentsList: ProductDesignerAttachmentsList;
    private _backgroundShapeList: ProductDesignerBackgroundShapeList;
    private _canvasElement: HTMLCanvasElement = document.createElement('canvas');
    private _canvas: Canvas;
    private _canvasUtils: ProductDesignerCanvasUtils;
    private _exportDialog: ProductDesignerExportDialog;
    private _previewDialog: ProductDesignerPreviewDialog;
    private _helpDialog: ProductDesignerHelpDialog;
    private _objectMenu: ProductDesignerObjectMenu;
    private _guidelinesHandler: ProductDesignerGuidelinesHandler;
    private _stateHistoryManager: ProductDesignerHistoryManager = new ProductDesignerHistoryManager();
    private t = createTranslator(NS.SHARED_ARCHIVE_PRODUCT_DESIGNER);
    private _loadedFontNames: string[] = [];
    private _fontData: { name: string; extension: string; data: ArrayBuffer }[] = [];
    private _mCalc: ProductDesignerMeasurementsCalculator = new ProductDesignerMeasurementsCalculator();
    private _rulers: ProductDesignerRulers = new ProductDesignerRulers();
    private _cropDialog: ImageCropDialog;
    private _containerSizeObserver: ResizeObserver = new ResizeObserver(this.onContainerResized.bind(this));
    private _mainContainerRef: Ref<HTMLDivElement> = createRef();
    private _canvasContainerRef: Ref<HTMLDivElement> = createRef();

    private render(): void {
        if (!this._isInitialized) {
            return;
        }

        render(this.template(this.t), this._root);

        if (!this._isRendered) {
            this._isRendered = true;
            this.initCanvas();

            render(this.template(this.t), this._root);

            // Select default background shape if shapes are enabled, then update background and overlay
            this.selectDefaultBackgroundShape().then(() => {
                this.updateCanvasOverlay().then(() => {
                    const addedDefaultText = this.addDefaultText();
                    // Seeding the default text selects it, which switches the active section to Text.
                    // When shapes are enabled the designer should open on the Background Shape section
                    // (set in initInternal), so re-assert it and deselect the seeded text.
                    if (addedDefaultText && this._productDetails?.printConfig.generalSettings.shapes?.some(s => s.enabled)) {
                        this.clearSelection();
                        this._activeSection = Section.BackgroundShape;
                        this.render();
                    }
                });
            });
        }
    }

    private refreshLists() {
        this._textList?.refresh();
        this._imageList?.refresh();
        this._shapeObjectList?.refresh();
        this._attachmentsList?.refresh();
        this._backgroundShapeList?.refresh();
    }

    private refreshEditors() {
        this._textEditor?.refresh();
        this._imageEditor?.refresh();
        this._shapeObjectEditor?.refresh();
    }

    private readAttributes() {
        const productId = this.getAttribute('product-id');
        if (productId) {
            this._productId = parseInt(productId);
        }

        this._config.useDefaultStyles = this.hasAttribute('default-styles');
        this._config.exclusionZoneMode = this.hasAttribute('exclusion-zone-mode');
        this._config.adminMode = this.hasAttribute('admin-mode');

        const exportFilenamePrefix = this.getAttribute('export-filename-prefix');
        if (exportFilenamePrefix) {
            this._config.exportFilenamePrefix = exportFilenamePrefix;
        }

        if (this._config.exclusionZoneMode && this._activeSection === Section.Text) {
            this._activeSection = Section.Image;
        }
    }

    private async initInternal() {
        if (this._isInitialized || this._productId <= 0) {
            return;
        }

        this._api ??= new ProductDesignerApi(this.errorReporter);

        await this.loadData();
        await this.updateFonts();
        await initI18Next(NS.SHARED_ARCHIVE_PRODUCT_DESIGNER);
        this.checkCanvasEnabled();
        this.initCroppingDialog();

        // Setup delayed observation of container size changes
        window.setTimeout(() => {
            this._containerSizeObserver.observe(this);
            this.onContainerResized();
        }, 100);

        // Set default active section based on available features
        if (this._productDetails?.printConfig.generalSettings.shapes?.some(s => s.enabled)) {
            this._activeSection = Section.BackgroundShape;
        }

        this._isInitialized = true;
    }

    private async loadData(): Promise<void> {
        this._api ??= new ProductDesignerApi(this.errorReporter);
        const fetched = await this._api.getProductDetails(this._productId);

        // Preserve the printConfig reference so sub-editors (text/image/shape) that
        // captured it at construction continue to see updated fields in-place.
        const keptPrintConfig = this._productDetails?.printConfig;
        this._productDetails = fetched;
        if (keptPrintConfig) {
            Object.assign(keptPrintConfig, fetched.printConfig);
            this._productDetails.printConfig = keptPrintConfig;
        }

        if (this._printConfigOverride) {
            Object.assign(this._productDetails.printConfig, this._printConfigOverride);
        }

        this.applyVariantOverrideToProductDetails();

        // Never use single color mode in exclusion zone mode
        if (this._config.exclusionZoneMode) {
            this._productDetails.printConfig.generalSettings.colorMode = ColorMode.BackgroundBased;
            this._productDetails.printConfig.advancedTextSettings.enableCustomColors = true;
            this._productDetails.printConfig.generalSettings.enableCustomColors = true;
            this._productDetails.printConfig.generalSettings.enablePredefinedColor = true;
        }

        this._productDetails.printConfig.advancedTextSettings.fonts ??= [];

        this._imageCategories = this._productDetails.printConfig.imageSettings.enableImageLibrary
            ? await this._api.getProductImageCategories(this._productId, this._config.exclusionZoneMode)
            : [];

        if (this._canvas) {
            this._canvasUtils = new ProductDesignerCanvasUtils(this._canvas, this._productDetails, this.t);
        }
    }

    private async updateFonts() {
        // Load uploaded fonts (always) and web-safe fonts referenced by the product. Arial is
        // always loaded as background fallback so text added without an explicit font picker
        // (when the product has zero configured fonts) still renders and can be converted to
        // paths on export.
        const config = this._productDetails.printConfig.advancedTextSettings;
        const fontFiles = (await this._api.getFontFiles(this._productDetails.productId)) ?? [];

        this._fontData = [];
        this._loadedFontNames = [];

        const fontPromises = fontFiles.map(async (fontFile) => {
            let fontUrl = fontFile.path;
            if (fontFile.modified) {
                const separator = fontUrl.includes('?') ? '&' : '?';
                const modifiedDate = new Date(fontFile.modified);
                const version = Math.floor(modifiedDate.getTime() / 1000);
                fontUrl += `${separator}v=${version}`;
            }

            const response = await fetch(fontUrl);
            const fontData = await response.arrayBuffer();
            const splitChar = fontFile.path.includes(".") ? "." : "-";
            const extension = fontFile.path.toLowerCase().split(splitChar).pop();
            this._fontData.push({ name: fontFile.name, extension, data: fontData });
        });

        const configFonts = new Set(config.fonts ?? []);
        const webSafeFiles = getWebSafeFontsFileNames().filter(f => f.name === 'Arial' || configFonts.has(f.name));
        const webSafeFontPromises = webSafeFiles.map(async (fontFile: { name: string, path: string }) => {
            let fontUrl = getFontPath(fontFile.path);
            const response = await fetch(fontUrl);
            const fontData = await response.arrayBuffer();
            const splitChar = fontFile.path.includes(".") ? "." : "-";
            const extension = fontFile.path.toLowerCase().split(splitChar).pop();
            this._fontData.push({ name: fontFile.name, extension, data: fontData });
        });

        await Promise.all([...fontPromises, ...webSafeFontPromises]);

        const fonts = this._fontData.map(font => new FontFace(font.name, font.data));
        await Promise.all(fonts.map(font => font.load()));

        fonts.forEach(font => {
            (document.fonts as any).add(font);
            this._loadedFontNames.push(font.family);
        });

        // Surface uploaded custom fonts in the selector. Web-safe entries already come from the
        // user's curated config list; Arial loaded as a background fallback stays hidden when
        // the product has zero configured fonts.
        if (fontFiles.length > 0) {
            const uploadedNames = fontFiles.map(f => f.name);
            config.fonts = [...new Set([...(config.fonts ?? []), ...uploadedNames])]
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        }
    }

    private async updateAttachmentsList() {
        this._attachmentsList = null;
        this.initAttachmentsList();
    }

    private checkCanvasEnabled() {
        if (this._productDetails.printConfig.mode !== PrintConfigMode.Advanced) {
            const message = this.t('canvasEditNotEnabled');
            this.errorReporter?.({ message });

            this.style.display = 'none';
        }
    }

    private initColorSection(): void {
        if (this._colorSection) {
            return;
        }

        this._colorSection = new ProductDesignerColorSection(this._canvas, this._productDetails.printConfig);
        this._colorSection.addEventListener('colorChange', this.onSingleColorChange.bind(this));

        // Make a simple call to get color here so that a color is assigned to canvas
        this._colorSection.color;
    }

    private initTextElements(): void {
        if (this._textEditor) {
            return;
        }

        this._textList = new ProductDesignerObjectList(
            this._canvas,
            this._config,
            this._canvasUtils, 
            Section.Text,
            this.onAddNewClicked.bind(this),
            this.deleteObject.bind(this),
            this.setActiveObject.bind(this));
        
        this._textEditor = new ProductDesignerTextEditor(this._canvas, this._config, this._productDetails.printConfig, this._mCalc, this._canvasUtils);
        this._textEditor.addEventListener('change', this.onEditorChange.bind(this));
    }

    private initImageElements(): void {
        if (this._imageEditor) {
            return;
        }

        this._imageList = new ProductDesignerObjectList(
            this._canvas,
            this._config,
            this._canvasUtils, 
            Section.Image,
            this.onAddNewClicked.bind(this),
            this.deleteObject.bind(this),
            this.setActiveObject.bind(this));

        this._imageEditor = new ProductDesignerImageEditor(
            this._canvas, 
            this._config,
            this._productDetails.printConfig, 
            this._imageCategories, 
            this.errorReporter, 
            this._mCalc);

        this._imageEditor.addEventListener('change', this.onEditorChange.bind(this));
    }

    private initShapeElements(): void {
        if (this._shapeObjectEditor) {
            return;
        }

        this._shapeObjectList = new ProductDesignerObjectList(
            this._canvas,
            this._config,
            this._canvasUtils, 
            Section.ShapeObject,
            this.onAddNewClicked.bind(this),
            this.deleteObject.bind(this),
            this.setActiveObject.bind(this));

        this._shapeObjectEditor = new ProductDesignerShapeObjectEditor(
            this._canvas, 
            this._config,
            this._productDetails.printConfig, 
            this._mCalc);

        this._shapeObjectEditor.addEventListener('change', this.onEditorChange.bind(this));
    }

    private initAttachmentsList() {
        if (this._attachmentsList) {
            return;
        }

        this._attachmentsList = new ProductDesignerAttachmentsList(
            this._canvasUtils,
            this.onAttachmentSelected.bind(this));
    }

    private async selectDefaultBackgroundShape() {
        const defaultId = this.resolveDefaultEnabledShapeId();
        if (defaultId !== null && !this._canvas.backgroundShapeId) {
            await this.onBackgroundShapeSelected(defaultId);
        } else {
            await this.updateCanvasBackground();
        }
    }

    /**
     * Picks the shape id that should be selected by default — the one flagged `isDefault`, falling
     * back to the first enabled shape. Returns null when no shapes are enabled. Used both for
     * initial selection and when loading an older template that pre-dates the backgroundShapeId field.
     */
    private resolveDefaultEnabledShapeId(): number | null {
        const enabledShapes = this._productDetails?.printConfig?.generalSettings?.shapes?.filter(s => s.enabled) ?? [];
        if (enabledShapes.length === 0) return null;
        return (enabledShapes.find(s => s.isDefault) ?? enabledShapes[0]).shapeId;
    }

    private initBackgroundShapeList() {
        if (this._backgroundShapeList) {
            return;
        }

        this._backgroundShapeList = new ProductDesignerBackgroundShapeList(
            this._canvas,
            this._productDetails.printConfig,
            this.onBackgroundShapeSelected.bind(this));
    }

    private initExportDialog() {
        if (this._exportDialog) {
            return;
        }

        this._exportDialog = new ProductDesignerExportDialog(this._canvas, this._config.exportFilenamePrefix, this.getSvg.bind(this));
        this._exportDialog.addDialogToBody = false;
    }

    private initPreviewDialog() {
        if (this._previewDialog) {
            return;
        }

        this._previewDialog = new ProductDesignerPreviewDialog(this._canvas, this.getSvg.bind(this), this._config.exportFilenamePrefix);
        this._previewDialog.addDialogToBody = false;
    }

    private initHelpDialog() {
        if (this._helpDialog) {
            return;
        }

        this._helpDialog = new ProductDesignerHelpDialog();
        this._helpDialog.addDialogToBody = false;
    }

    private initCroppingDialog() {
        if (this._cropDialog) {
            return;
        }

        this._cropDialog = new ImageCropDialog();
        this._cropDialog.addDialogToBody = false;
    }

    private initObjectMenu() {
        this._objectMenu = new ProductDesignerObjectMenu(this._canvas, this._config, this._cropDialog, this.onCloneObjectClicked.bind(this));
        this._objectMenu.useDefaultStyles = this._config.useDefaultStyles;
        this._objectMenu.addEventListener('change', this.onObjectMenuChange.bind(this));
    }

    private initGuidesHandler() {
        if (this._guidelinesHandler) {
            return;
        }

        this._guidelinesHandler = new ProductDesignerGuidelinesHandler(this._canvas);
    }

    private initCanvas() {
        if (this._canvas) {
            return;
        }

        this._canvasElement.id = 'product-designer-canvas';
        this._canvas = new Canvas(this._canvasElement, 
            { 
                targetFindTolerance: 10, 
                selection: false, 
                allowTouchScrolling: true 
            });

        this._canvas.on('selection:created', this.onCanvasSelectionChanged.bind(this));
        this._canvas.on('selection:updated', this.onCanvasSelectionChanged.bind(this));
        this._canvas.on('selection:cleared', this.onCanvasSelectionCleared.bind(this));
        this._canvas.on('object:modified', this.onCanvasObjectModified.bind(this));
        this._canvas.on('object:removed', this.onCanvasObjectRemoved.bind(this));
        this._canvas.on('text:changed', () => this._objectMenu.updatePosition());

        document.addEventListener('keydown', this.onDocumentKeyDown.bind(this));

        this._canvasUtils = new ProductDesignerCanvasUtils(this._canvas, this._productDetails, this.t);

        this.updateCanvasSize();
        this.initBackgroundShapeList();
        this.initColorSection();
        this.initTextElements();
        this.initImageElements();
        this.initShapeElements();
        this.initAttachmentsList();
        this.initObjectMenu();
        this.initGuidesHandler();
        this.initExportDialog();
        this.initPreviewDialog();
        this.initHelpDialog();

        this._stateHistoryManager.onStateChanged = () => {
            if (this._canvas) {
                this._canvas.confirmed = false;
            }
        };

        if (this._design) {
            this.loadState(this._design).then(() => {
                this._stateHistoryManager.addState(this.getDesign());
            });
        } else {
            this._stateHistoryManager.addState(this.getDesign());
        }
    }

    private updateCanvasSize() {
        const parent = this._canvasElement.closest('.editor-canvas') as HTMLElement;

        if (!parent || !this._productDetails) {
            return;
        }

        const width = DEFAULT_CANVAS_WIDTH;
        const height = DEFAULT_CANVAS_HEIGHT;

        parent.style.width = `${width}px`;
        parent.style.height = `${height}px`;

        this._canvas.setDimensions({width, height});
    }

    private async updateCanvasBackground() {
        if (!this._canvas) {
            return;
        }

        // Built-in shapes render as fabric primitives sized to the configured background dims —
        // no server image fetch unless the user / global definition supplies an explicit override.
        if (this.tryRenderBuiltInShapeBackground()) {
            return;
        }

        // In DualColor mode, apply the background color (monoColor) as a colored rect behind the background image
        if (this._productDetails?.printConfig.generalSettings.colorMode === ColorMode.DualColor && this._canvas.monoColor) {
            const imageId = this._productDetails.printConfig.generalSettings.canvasBackgroundImageId;

            if (!imageId) {
                // No background image — use a colored rect as the background
                const left = this._mCalc.getPixelPosX(0);
                const top = this._mCalc.getPixelPosY(0);
                const width = this._mCalc.getPixelWidth(this._productDetails.printConfig.generalSettings.backgroundWidthMm);
                const height = this._mCalc.getPixelHeight(this._productDetails.printConfig.generalSettings.backgroundHeightMm);

                this._canvas.backgroundImage = new fabric.Rect({
                    left,
                    top,
                    width,
                    height,
                    fill: this._canvas.monoColor,
                });

                this.updateMeasurementsCalculator(this._canvas.backgroundImage);
                this.render();

                return;
            }

            // Has background image — load it with the background color applied via monoColor parameter
            // Use threshold 255 to color all pixels while preserving transparency
            const url = getImageUrlFromId(imageId, undefined, undefined, this._canvas.monoColor, 255);
            const imageResponse = await fetch(url);
            const mimeType = imageResponse.headers.get('Content-Type') || 'image/png';
            let backgroundImage: FabricObject | null = null;

            if (mimeType === SVG_MIME_TYPE) {
                backgroundImage = new Group([]);
                const { objects } = await fabric.loadSVGFromString(await imageResponse.text());
                await backgroundImage.updateSvg(objects, null);
            } else {
                backgroundImage = await FabricImage.fromURL(url, {}, {
                    canvas: this._canvas,
                });
            }

            this._canvas.backgroundImage = backgroundImage;

            // Stretch the image to fill the shape's mm-aspect box (X/Y scaled independently).
            const box = this.getBackgroundFillBox();
            backgroundImage.scaleX = box.width / backgroundImage.width;
            backgroundImage.scaleY = box.height / backgroundImage.height;
            this._canvas.centerObject(backgroundImage);
            this.updateMeasurementsCalculator(backgroundImage);
            this.render();

            return;
        }

        const imageId = this._productDetails ? this._productDetails.printConfig.generalSettings.canvasBackgroundImageId : this._variantOverride?.canvasBackgroundImageId;

        if (!imageId) {
            this._canvas.backgroundImage = null;
            return;
        }

        const url = getImageUrlFromId(imageId);
        const imageResponse = await fetch(url);
        const mimeType = imageResponse.headers.get('Content-Type') || 'image/png';
        let backgroundImage: FabricObject | null = null;
        
        if (mimeType === SVG_MIME_TYPE) {
            backgroundImage = new Group([]);
            const { objects } = await fabric.loadSVGFromString(await imageResponse.text());
            await backgroundImage.updateSvg(objects, null);
        } else {
            backgroundImage = await FabricImage.fromURL(url, {}, {
                canvas: this._canvas,
            });
        }

        this._canvas.backgroundImage = backgroundImage;

        // Stretch the image to fill the shape's mm-aspect box (X/Y scaled independently).
        const box = this.getBackgroundFillBox();
        backgroundImage.scaleX = box.width / backgroundImage.width;
        backgroundImage.scaleY = box.height / backgroundImage.height;

        this._canvas.centerObject(backgroundImage);

        this.updateMeasurementsCalculator(backgroundImage);

        this.render();
    }

    /**
     * Renders one of the four built-in shapes (rectangle, rounded rectangle, circle, triangle) as a
     * fabric primitive sized to the configured backgroundWidthMm × backgroundHeightMm. Skipped when
     * no shape is active, the active shape isn't a built-in, or an explicit background image is set
     * (definition.backgroundImageId or per-product backgroundImageIdOverride) — in those cases the
     * caller falls through to the existing image-loading paths.
     *
     * Returns true when the built-in primitive was rendered (caller should not continue).
     */
    private tryRenderBuiltInShapeBackground(): boolean {
        const shapeId = this._canvas.backgroundShapeId;
        if (!isBuiltInShapeId(shapeId)) return false;

        const settings = this._productDetails?.printConfig?.generalSettings;
        const shapeConfig = settings?.shapes?.find(s => s.shapeId === shapeId);
        const shapeDefinition = this._productDetails?.printConfig?.shapesLibrary?.find(d => d.id === shapeId);

        // Explicit image override (global definition or per-product) takes precedence — fall through
        // so the existing image-loading branches handle it.
        if (shapeConfig?.backgroundImageIdOverride || shapeDefinition?.backgroundImageId) return false;

        const widthMm = settings?.backgroundWidthMm;
        const heightMm = settings?.backgroundHeightMm;
        if (!widthMm || !heightMm) return false;

        // Fill: monoColor when DualColor mode is in effect with a chosen color, otherwise white.
        // No stroke so the primitive matches the visual the SVG sources used.
        const fill = settings.colorMode === ColorMode.DualColor && this._canvas.monoColor
            ? this._canvas.monoColor
            : '#FFFFFF';

        // Build the primitive in mm-as-pixels logical units; the existing fit-canvas scale below
        // converts those units to display pixels while preserving the configured aspect ratio.
        const widthUnits = Number(widthMm);
        const heightUnits = Number(heightMm);
        const backgroundImage = createBuiltInShape(shapeId, widthUnits, heightUnits, fill);
        if (!backgroundImage) return false;

        this._canvas.backgroundImage = backgroundImage;

        // Built-in primitive is built at mm-aspect, so this fills the same mm-aspect box without distortion.
        const box = this.getBackgroundFillBox();
        backgroundImage.scaleX = box.width / backgroundImage.width;
        backgroundImage.scaleY = box.height / backgroundImage.height;
        this._canvas.centerObject(backgroundImage);
        this.updateMeasurementsCalculator(backgroundImage);
        this.render();

        return true;
    }

    /**
     * Computes the box a shape background fills: the largest box with the configured
     * backgroundWidthMm:backgroundHeightMm aspect that fits inside the canvas, scaled by
     * BACKGROUND_MAX_SIZE_FACTOR to keep a margin. Images are then stretched (X/Y independently)
     * into this box so they fill the shape while the shape keeps its real proportions.
     * Falls back to the full margin box when no valid mm dimensions are configured.
     */
    private getBackgroundFillBox(): { width: number; height: number } {
        const settings = this._productDetails?.printConfig?.generalSettings;
        const maxWidth = this._canvas.width * this.BACKGROUND_MAX_SIZE_FACTOR;
        const maxHeight = this._canvas.height * this.BACKGROUND_MAX_SIZE_FACTOR;

        const widthMm = Number(settings?.backgroundWidthMm);
        const heightMm = Number(settings?.backgroundHeightMm);
        if (!widthMm || !heightMm) {
            return { width: maxWidth, height: maxHeight };
        }

        const mmAspect = widthMm / heightMm;
        const boxAspect = maxWidth / maxHeight;
        return mmAspect > boxAspect
            ? { width: maxWidth, height: maxWidth / mmAspect }
            : { width: maxHeight * mmAspect, height: maxHeight };
    }

    private updateMeasurementsCalculator(bgImage: FabricObject) {
        const context = {
            posX: bgImage.left,
            posY: bgImage.top,
            width: bgImage.width * bgImage.scaleX,
            height: bgImage.height * bgImage.scaleY,
            physicalWidth: this._productDetails.printConfig.generalSettings.backgroundWidthMm,
            physicalHeight: this._productDetails.printConfig.generalSettings.backgroundHeightMm
        };

        this._mCalc.context = context;

        if (context.physicalWidth > 0 || context.physicalHeight > 0) {
            this._rulers.setContext(context);
        }
    }

    private async updateCanvasOverlay() {
        if (!this._canvas) {
            return; 
        }

        // Clear existing exclusion zones
        this._canvas.overlayImage = null;

        if (this._config.exclusionZoneMode) {
            return;
        }

        // Create a new overlay group for exclusion zones
        const overlayGroup = new Group([], {
            selectable: false,
            evented: false,
            hasControls: false,
            hasBorders: false
        });

        this._canvas.overlayImage = overlayGroup;

        const exclusionZones = this._productDetails.exclusionZones?.filter(zone => !zone.toProductId) || [];

        if (this._canvas.attachmentZoneId) {
            const zone = this._productDetails.exclusionZones.find(z => z.id === this._canvas.attachmentZoneId);
            if (zone) {
                exclusionZones.push(zone);
            }
        }

        if (exclusionZones.length === 0) {
            return;
        }

        const bg = this._canvas.backgroundImage;
        const bgSize = bg ? {
            width: bg.width * bg.scaleX,
            height: bg.height * bg.scaleY
        } : null;

        // Load all images
        await Promise.all(
            exclusionZones
                .filter((zone) => zone.imageItemId > 0)
                .map(async (zone) => {
                    const imageUrl = getImageUrlFromId(zone.imageItemId, undefined, zone.designHash);
                    const svgGroup = new Group([]);
                    await svgGroup.updateSvgUrl(imageUrl);
                    
                    // Re-scale the SVG to have exact same size as background image
                    if (bgSize) {
                        const scale = bgSize.width / svgGroup.width;
                        svgGroup.scale(scale);
                    }

                    // Center the exclusion zone on the canvas
                    svgGroup.setXY(this._canvas.getCenterPoint(), 'center', 'center');

                    overlayGroup.add(svgGroup);
                })
            );

        this._canvas.renderAll();
    }

    private clearSelection() {
        this._activeObject = null;
        this._textEditor.text = null;
        this._imageEditor.image = null;
        this._shapeObjectEditor.shape = null;

        this._canvas.discardActiveObject();

        this._canvas.renderAll();
        this.render();
    }

    private setActiveObject(object: FabricObject) {
        const section = getSectionFromObjectType(object.type, !!object.isSvgGroup);
        this._activeObject = object;
        this._activeSection = section;
        this._canvas.setActiveObject(object); 

        switch (section) {
            case Section.Text:
                this._textEditor.text = object as Textbox | CurvedText;
                break;
            case Section.Image:
                this._imageEditor.image = object as FabricImage;
                break;
            case Section.ShapeObject:
                this._shapeObjectEditor.shape = object as FabricObject;
                break;
        }

        if (object) {
            updateObjectCanvasLocks(this._config.adminMode, object);
            updateObjectControlsVisibility(this._config.adminMode, object);
        }

        this._canvas.renderAll();
        this.render();
    }

    private deleteActiveObject() {
        if (!this._activeObject) {
            return;
        }

        const id = this._activeObject.id;
        this.deleteObject(id);
    }

    private deleteObject(id: number) {
        const obj = this._canvas.getObjects().find(o => o.id === id);
        this._canvas.remove(obj);

        this._activeObject = null;
        this._textEditor.text = null;
        this._imageEditor.image = null;
        this._shapeObjectEditor.shape = null;

        this._canvas.renderAll();
        this._stateHistoryManager.addState(this.getDesign());
        this.render();
    }

    private async historyUndo() {
        const previousState = this._stateHistoryManager.undo();

        if (previousState) {
            this._design = previousState;
            await this.loadState(previousState);
            await this.updateCanvasBackground();
            await this.updateCanvasOverlay();
            this._canvas.renderAll();
            this.render();
            this.refreshLists();
        }
    }

    private async historyRedo() {
        const nextState = this._stateHistoryManager.redo();
        if (nextState) {
            this._design = nextState;
            await this.loadState(nextState);
            await this.updateCanvasBackground();
            await this.updateCanvasOverlay();
            this._canvas.renderAll();
            this.render();
            this.refreshLists();
        }
    }

    private async loadState(state: string) {
        if (!this._canvas || !state) {
            return;
        }

        // Clear background and overlay images
        const stateObj = JSON.parse(state);
        if (stateObj.backgroundImage) {
            delete stateObj.backgroundImage;
        }

        if (stateObj.overlayImage) {
            delete stateObj.overlayImage;
        }

        try {
            await this._canvas.loadFromJSON(JSON.stringify(stateObj), (_, obj) => {
                const o = obj as FabricObject;
                updateObjectControlsVisibility(this._config.adminMode, o);
                updateObjectCanvasLocks(this._config.adminMode, o);
            });
        }
        catch (error) {
            console.error('Failed to load canvas state:', error);
        }

        // Restore canvas custom properties
        const parsedState = JSON.parse(state);
        this._canvas.attachmentZoneId = parsedState.attachmentZoneId || null;
        // Older templates pre-date the backgroundShapeId field — fall back to the configured default
        // (or first) enabled shape so loading them doesn't leave the sidebar selection empty.
        this._canvas.backgroundShapeId = parsedState.backgroundShapeId || this.resolveDefaultEnabledShapeId();

        // Restore background image from selected shape
        if (this._canvas.backgroundShapeId) {
            const shapeConfig = this._productDetails.printConfig.generalSettings.shapes?.find(s => s.shapeId === this._canvas.backgroundShapeId);
            const shapeDefinition = this._productDetails.printConfig.shapesLibrary?.find(d => d.id === this._canvas.backgroundShapeId);
            const imageId = shapeConfig?.backgroundImageIdOverride || shapeDefinition?.backgroundImageId || 0;
            this._productDetails.printConfig.generalSettings.canvasBackgroundImageId = imageId;
        }

        // Determine which ColorMode the design was saved in. Prefer the explicit field, fall back
        // to inference from the stored color fields (legacy designs predate the colorMode field).
        const currentColorMode = this._productDetails.printConfig.generalSettings.colorMode;
        const savedColorMode: ColorMode | null = (parsedState.colorMode as ColorMode)
            ?? (parsedState.dualForegroundColor ? ColorMode.DualColor
                : parsedState.monoColor ? ColorMode.SingleColor
                : ColorMode.BackgroundBased);
        const modeChanged = savedColorMode !== currentColorMode;

        // Resolve fallback colors from the configured palette when no explicit defaults are set.
        const generalSettings = this._productDetails.printConfig.generalSettings;
        const fallbackPaletteType = currentColorMode === ColorMode.DualColor ? ColorPaletteType.DualColor : ColorPaletteType.SingleColor;
        const fallbackPalette = this._productDetails.printConfig.colorPalettes?.find(p =>
            (p.id === generalSettings.defaultPaletteId) && (!p.type || p.type === fallbackPaletteType))
            ?? this._productDetails.printConfig.colorPalettes?.find(p => !p.type || p.type === fallbackPaletteType);
        const fallbackColor = fallbackPalette?.colors?.find(c => c.hex !== '#00000000');

        // When the saved mode differs from the current product mode the saved colour fields no
        // longer match what each slot means (e.g. SingleColor.monoColor was a foreground, but
        // DualColor treats monoColor as the background). Ignore the saved colours in that case
        // and fall back to configured defaults / palette so the design isn't loaded with stale
        // values that produce e.g. black-on-black text.
        const savedMonoColor = modeChanged ? null : parsedState.monoColor;
        const savedDualForegroundColor = modeChanged ? null : parsedState.dualForegroundColor;

        // Restore canvas-level colors per the *current* config — when the saved mode differs we
        // bring the design forward into the new mode using configured defaults.
        if (currentColorMode === ColorMode.DualColor) {
            this._canvas.monoColor = savedMonoColor
                || generalSettings.defaultColorCode
                || fallbackColor?.hex
                || null;
            this._canvas.dualForegroundColor = savedDualForegroundColor
                || generalSettings.defaultTextColorCode
                || fallbackColor?.textHex
                || fallbackColor?.hex
                || null;
        } else if (currentColorMode === ColorMode.SingleColor) {
            this._canvas.monoColor = savedMonoColor
                || generalSettings.defaultColorCode
                || fallbackColor?.hex
                || null;
            this._canvas.dualForegroundColor = null;
        } else {
            // BackgroundBased: no canvas-wide color; objects keep their own colors.
            this._canvas.monoColor = null;
            this._canvas.dualForegroundColor = null;
        }

        this._canvas.colorMode = currentColorMode ?? null;

        // When the mode changes, re-color all objects (text fg, shape fill/stroke, image tint)
        // and the background image so they match the new mode's palette.
        if (modeChanged && currentColorMode !== ColorMode.BackgroundBased) {
            await this.updateObjectsSingleColor();
        }
    }

    private canUndo(): boolean {
        return this._stateHistoryManager.canUndo();
    }

    private canRedo(): boolean {
        return this._stateHistoryManager.canRedo();
    }

    private async clear(clearHistory: boolean, addDefaultText: boolean) {
        if (!this._canvas) {
            return;
        }

        if (clearHistory) {
            this._stateHistoryManager.clearHistory();
        }

        this._activeSection = this._config.exclusionZoneMode ? Section.Image
            : this._productDetails?.printConfig.generalSettings.shapes?.some(s => s.enabled) ? Section.BackgroundShape
            : Section.Text;

        this._canvas.clear();
        this._canvas.attachmentZoneId = null;
        delete this._canvas.backgroundShapeId;
        delete this._canvas.monoColor;
        delete this._canvas.dualForegroundColor;

        // Re-initialize color before updating background (dual color mode needs monoColor)
        this._colorSection.color;

        // Select default background shape (also updates background) or just update background
        await this.selectDefaultBackgroundShape();
        await this.updateCanvasOverlay();

        if (addDefaultText) {
            this.addDefaultText();
        }

        this.refreshLists();

        this._canvas.renderAll();
    }

    private addDefaultText(): boolean {
        // Use canvas object count rather than this._design.length — getDesign() writes the
        // serialized empty canvas into this._design as a side effect of the state-history
        // bootstrap, so checking string length here would always be truthy on a fresh open
        // and the default text would never be added.
        if (!this._canvas || !this._textEditor || this._canvas.getObjects().length > 0 || this.startDesign?.length) {
            return false;
        }

        this._textEditor.addNewText();
        return true;
    }

    /* #endregion */

    /* connectedCallback */
    async connectedCallback() {
        this.readAttributes();

        if (this._config.useDefaultStyles) {
            this.attachShadow({ mode: 'open' });
            this._root = this.shadowRoot;
        } else {
            this._root = this;
        } 

        if (this._productId > 0) {
            await this.initInternal();
        }

        this.render();
    }
}

customElements.define('product-designer', ProductDesigner);